/** A study listed in a disc's DICOMDIR (same fields as the study picker of "Process as Medical Disc"). */
export interface DiscStudy {
  studyUID: string;
  patientName: string;
  studyDate: string; // DICOM date, YYYYMMDD
  studyDescription: string;
  modalities: string[];
  imageCount: number;
}

/** A Clio folder that contains a DICOMDIR. */
export interface MedicalDisc {
  folderId: string;
  name: string;
  path: string;
  dicomdirId: string;
  studies: DiscStudy[];
  /** Set when the disc index could not be read; the disc is listed but cannot be imported. */
  error?: string;
}

/** Selected studies per disc: { [discFolderId]: { [studyUID]: true } } */
export type DiscSelection = { [folderId: string]: { [studyUID: string]: boolean } };

export type PickState = 'none' | 'some' | 'all';

export interface ImportRequest {
  disc: MedicalDisc;
  studyUIDs: string[];
}

export interface SelectionSummary {
  studies: number;
  images: number;
  discs: number;
  wholeDiscs: number;
}

export function selectedUIDs(disc: MedicalDisc, selection: DiscSelection): string[] {
  const picked = selection[disc.folderId] || {};
  return disc.studies.filter(s => picked[s.studyUID]).map(s => s.studyUID);
}

export function discPickState(disc: MedicalDisc, selection: DiscSelection): PickState {
  const n = selectedUIDs(disc, selection).length;
  if (n === 0) {
    return 'none';
  }
  return n === disc.studies.length ? 'all' : 'some';
}

/** Ticks or unticks every study of a disc. Returns a new selection. */
export function setDiscSelected(disc: MedicalDisc, selection: DiscSelection, checked: boolean): DiscSelection {
  const picked: { [uid: string]: boolean } = {};
  if (checked) {
    disc.studies.forEach(s => (picked[s.studyUID] = true));
  }
  return { ...selection, [disc.folderId]: picked };
}

export function setStudySelected(disc: MedicalDisc, selection: DiscSelection, studyUID: string, checked: boolean): DiscSelection {
  const picked = { ...(selection[disc.folderId] || {}) };
  if (checked) {
    picked[studyUID] = true;
  } else {
    delete picked[studyUID];
  }
  return { ...selection, [disc.folderId]: picked };
}

export function summarizeSelection(discs: MedicalDisc[], selection: DiscSelection): SelectionSummary {
  const summary: SelectionSummary = { studies: 0, images: 0, discs: 0, wholeDiscs: 0 };
  discs.forEach(disc => {
    const uids = selectedUIDs(disc, selection);
    if (uids.length === 0) {
      return;
    }
    summary.discs++;
    summary.studies += uids.length;
    if (uids.length === disc.studies.length) {
      summary.wholeDiscs++;
    }
    disc.studies.filter(s => uids.includes(s.studyUID)).forEach(s => (summary.images += s.imageCount));
  });
  return summary;
}

/** One request per disc that has at least one selected study. A whole disc sends every study UID. */
export function buildImportRequests(discs: MedicalDisc[], selection: DiscSelection): ImportRequest[] {
  return discs
    .map(disc => ({ disc, studyUIDs: selectedUIDs(disc, selection) }))
    .filter(r => r.studyUIDs.length > 0);
}

/** 20230404 -> 2023-04-04. Anything else is returned unchanged. */
export function formatDicomDate(value: string): string {
  const m = /^(\d{4})(\d{2})(\d{2})$/.exec((value || '').trim());
  return m ? `${m[1]}-${m[2]}-${m[3]}` : value || '';
}
